import { redirect, useLoaderData } from 'react-router';

import type { Route } from './+types/topic';
import { withScrollytellingBridge } from '@/chat/scrollytelling-bridge.server';
import { metaFor } from '@/cms/meta';
import { moduleTopicUids, topicModuleKey, validTopicsFor } from '@/cms/topics';
import { getTopic, getTopics } from '@/cms/topics.server';
import { ChatModule } from '@/components/chat/chat-module';
import { guardTopic } from '@/store/guards';
import { DEFAULT_BUDDY, topicProfile, useIsCourseComplete, useUserStore } from '@/store/user';
import { themeFromPathname } from '@/utils/theme';

/**
 * `/h/:theme/learn/topics/:slug` — a module, played as the Evolved Chat.
 *
 * The topic is read on the server; who the learner is, and therefore whether
 * this module is theirs to open, is only knowable in the browser, so the guard
 * and the journey position are computed in `clientLoader`.
 */

export function meta({ matches, data }: Route.MetaArgs) {
  const siteSettings = matches[0]?.data.siteSettings;
  const title = data?.title ?? 'Beach Passport';

  return siteSettings ? metaFor({ siteSettings, title }) : [{ title }];
}

export async function loader({ params }: Route.LoaderArgs) {
  const [topic, topics] = await Promise.all([getTopic(params.slug), getTopics()]);

  if (!topic) {
    throw new Response('Not found', { status: 404 });
  }

  return { topic: await withScrollytellingBridge(topic), topics };
}

export async function clientLoader({ serverLoader, params }: Route.ClientLoaderArgs) {
  const { topic, topics } = await serverLoader();
  const state = useUserStore.getState();
  const profile = topicProfile(state);
  const valid = profile ? validTopicsFor(profile, topics) : [];

  const guard = guardTopic({
    profile: state.profile,
    validModuleSlugs: valid.map((candidate) => candidate.uid),
    slug: params.slug,
  });

  if (guard) {
    throw redirect(guard.to);
  }

  const index = valid.findIndex((candidate) => candidate.uid === params.slug);
  // Up next is the first unscored module after this one, wrapping to earlier
  // ones, so a replay never points at a finished module.
  const next =
    valid.slice(index + 1).find((candidate) => state.modules[topicModuleKey(candidate)]?.bestScore === undefined) ??
    valid.slice(0, index).find((candidate) => state.modules[topicModuleKey(candidate)]?.bestScore === undefined);

  return {
    topic,
    title: topic.data.title?.trim() || topic.uid,
    meta: {
      moduleKey: topicModuleKey(topic),
      moduleIndex: index + 1,
      moduleTotal: valid.length,
      title: topic.data.title?.trim() || topic.uid,
      upNext: next ? next.data.title?.trim() || next.uid : null,
    },
    validModuleKeys: valid.map(topicModuleKey),
    // The same module in every other language, so the header's language switch
    // can keep the learner inside it (see `app/chat/language-switch.ts`).
    moduleTopicUids: moduleTopicUids(topics, topic),
  };
}

// Runs on a hard refresh too, so a learner never sees someone else's module.
clientLoader.hydrate = true as const;

/** Invisible: the guard resolves before the chat paints. */
export function HydrateFallback() {
  return null;
}

export default function Topic({ params }: Route.ComponentProps) {
  const { topic, meta: scriptMeta, validModuleKeys, moduleTopicUids: topicUids } = useLoaderData<typeof clientLoader>();
  const profile = useUserStore((state) => state.profile);
  // Once `finishModule` has written this module's score, this flips true and
  // the completion card hands off to the passport instead of the journey.
  const courseComplete = useIsCourseComplete(validModuleKeys);

  const theme = themeFromPathname(`/h/${params.theme}`) ?? 'ocean';

  return (
    <ChatModule
      topic={topic}
      meta={scriptMeta}
      theme={theme}
      buddy={profile?.buddy ?? DEFAULT_BUDDY}
      language={profile?.language ?? 'en'}
      audience={profile?.audience ?? 'adult'}
      completesCourse={courseComplete}
      topicUids={topicUids}
    />
  );
}
